<script setup lang="ts">
import { useTresContext, useRenderLoop } from '@tresjs/core';

import type { Camera, WebGLRenderer, Scene } from 'three';
import { Vector2 } from 'three';

import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';

const ctx = useTresContext();

const scene = ctx.scene.value;
const camera = ctx.camera.value;
const renderer = ctx.renderer.value;

const { onLoop } = useRenderLoop();

const props = defineProps<{
  tier: 'high' | 'medium' | 'low';
}>();

// Конфигурационные параметры — адаптивные под tier устройства
const CONFIG = {
  MAX_WAVES: props.tier === 'low' ? 15 : props.tier === 'medium' ? 30 : 50,
  WAVE_LIFETIME: 0.51,
  BASE_AMPLITUDE: 0.01,
  MAX_MOUSE_SPEED: 2.0,
  LERP_FACTOR: 0.2,
  FREQUENCY: 15.01,
  WAVE_SPEED: 10.01,
  DECAY_FACTOR: 20.01,
  MIN_ACTIVATION_SPEED: 0.01,
  // Максимальный deltaTime (сек) — защита от всплесков при возврате на вкладку
  MAX_DELTA_TIME: 0.1,
  // Минимальный интервал между обработкой mousemove (мс), ~120Hz
  MOUSE_THROTTLE_MS: 8,
};

const { width, height } = useWindowSize();
const aspectRatio = computed(() => width.value / height.value);

const createWaveEffect = (
  renderer: WebGLRenderer,
  scene: Scene,
  camera: Camera,
) => {
  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));

  const wavePass = new ShaderPass({
    uniforms: {
      tDiffuse: { value: null },
      uTime: { value: 0 },
      uAspect: { value: aspectRatio.value },
      uWavePoints: { value: new Float32Array(CONFIG.MAX_WAVES * 4) },
      uWaveCount: { value: 0 },
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform sampler2D tDiffuse;
      uniform float uTime;
      uniform float uAspect;
      uniform float uWavePoints[${CONFIG.MAX_WAVES * 4}];
      uniform int uWaveCount;
      varying vec2 vUv;

      void main() {
          vec2 uv = vUv;
          vec2 totalDistortion = vec2(0.0);
          float currentTime = uTime;

          for(int i = 0; i < ${CONFIG.MAX_WAVES}; i++) {
              if(i >= uWaveCount) break;

              int base = i * 4;
              vec2 wavePos = vec2(uWavePoints[base], uWavePoints[base + 1]);
              float waveTime = uWavePoints[base + 2];
              float velocity = uWavePoints[base + 3];

              float timeDelta = currentTime - waveTime;
              float life = 1.0 - clamp(timeDelta / ${CONFIG.WAVE_LIFETIME}, 0.0, 1.0);

              if(life <= 0.0) continue;

              vec2 toWave = (uv - wavePos) * vec2(uAspect, 1.0);
              // distSq avoids sqrt in hot loop; guard with epsilon²
              float distSq = dot(toWave, toWave);
              float dist = distSq > 0.000001 ? sqrt(distSq) : 0.001;

              float amplitude = ${CONFIG.BASE_AMPLITUDE} * velocity;
              float waveFactor = sin(dist * ${CONFIG.FREQUENCY} - timeDelta * ${CONFIG.WAVE_SPEED});

              totalDistortion += vec2(waveFactor, cos(waveFactor))
                  * amplitude * life / (dist * ${CONFIG.DECAY_FACTOR} + 1.0);
          }

          uv = clamp(uv + totalDistortion, 0.0, 1.0);
          gl_FragColor = texture2D(tDiffuse, uv);
      }
    `,
  });

  composer.addPass(wavePass);

  return composer;
};

if (!camera) throw new Error('camera is required');

const composer = createWaveEffect(renderer, scene, camera);

// --- Кольцевой буфер волн (без аллокаций в рантайме) ---
const waveBuffer = new Float32Array(CONFIG.MAX_WAVES * 4); // x, y, time, velocity
let waveWriteIdx = 0;
let waveActiveCount = 0;

// Переиспользуемый буфер для передачи в шейдер
const shaderWaveData = new Float32Array(CONFIG.MAX_WAVES * 4);

// Состояние курсора
const lastMousePos = new Vector2(0.5, 0.5);
const targetMousePos = new Vector2(0.5, 0.5);
const currentMousePos = new Vector2(0.5, 0.5);
let lastMouseTime = performance.now();
let lastMouseMoveTime = 0;
let isPaused = false;

// --- Throttled обработчики событий ---
const onMouseMove = (e: MouseEvent) => {
  const now = performance.now();
  if (now - lastMouseMoveTime < CONFIG.MOUSE_THROTTLE_MS) return;
  lastMouseMoveTime = now;

  targetMousePos.set(
    e.clientX / window.innerWidth,
    1 - e.clientY / window.innerHeight,
  );
};

const onTouchMove = (e: TouchEvent) => {
  const touch = e.touches[0];
  if (!touch) return;

  const now = performance.now();
  if (now - lastMouseMoveTime < CONFIG.MOUSE_THROTTLE_MS) return;
  lastMouseMoveTime = now;

  targetMousePos.set(
    touch.clientX / window.innerWidth,
    1 - touch.clientY / window.innerHeight,
  );
};

// --- Visibility API: пауза при уходе с вкладки ---
const onVisibilityChange = () => {
  if (document.hidden) {
    isPaused = true;
  } else {
    // Сброс таймингов для предотвращения всплеска deltaTime
    const now = performance.now();
    lastMouseTime = now;
    isPaused = false;
  }
};

document.addEventListener('mousemove', onMouseMove);
document.addEventListener('touchmove', onTouchMove, { passive: true });
document.addEventListener('visibilitychange', onVisibilityChange);

// --- Анимационный цикл ---
const tick = () => {
  const now = performance.now();

  // Интерполяция позиции курсора
  currentMousePos.lerp(targetMousePos, CONFIG.LERP_FACTOR);

  // Расчёт скорости курсора (с clamped deltaTime)
  const deltaTime = Math.min((now - lastMouseTime) / 1000, CONFIG.MAX_DELTA_TIME);
  const distance = currentMousePos.distanceTo(lastMousePos);
  const velocity =
    deltaTime > 0
      ? Math.min(distance / deltaTime, CONFIG.MAX_MOUSE_SPEED) /
        CONFIG.MAX_MOUSE_SPEED
      : 0;

  // Добавление новой волны в кольцевой буфер
  if (velocity > CONFIG.MIN_ACTIVATION_SPEED) {
    const idx = waveWriteIdx * 4;
    waveBuffer[idx] = currentMousePos.x;
    waveBuffer[idx + 1] = currentMousePos.y;
    waveBuffer[idx + 2] = now / 1000;
    waveBuffer[idx + 3] = velocity;

    waveWriteIdx = (waveWriteIdx + 1) % CONFIG.MAX_WAVES;
    if (waveActiveCount < CONFIG.MAX_WAVES) waveActiveCount++;

    lastMousePos.copy(currentMousePos);
    lastMouseTime = now;
  }

  // Упаковка активных волн в переиспользуемый буфер
  const nowSec = now / 1000;
  let activeIdx = 0;

  for (let i = 0; i < waveActiveCount; i++) {
    const base = i * 4;
    const waveTime = waveBuffer[base + 2]!;
    const age = nowSec - waveTime;

    if (age >= 0 && age < CONFIG.WAVE_LIFETIME) {
      const out = activeIdx * 4;
      shaderWaveData[out] = waveBuffer[base]!;
      shaderWaveData[out + 1] = waveBuffer[base + 1]!;
      shaderWaveData[out + 2] = waveTime;
      shaderWaveData[out + 3] = waveBuffer[base + 3]!;
      activeIdx++;
    }
  }

  // Обновление uniform-ов шейдера
  const wavePass = composer.passes[1] as ShaderPass;
  wavePass.uniforms.uWavePoints!.value = shaderWaveData;
  wavePass.uniforms.uWaveCount!.value = activeIdx;
  wavePass.uniforms.uTime!.value = nowSec;
  wavePass.uniforms.uAspect!.value = aspectRatio.value;

  composer.render();
};

onLoop(() => {
  if (!isPaused) {
    tick();
  }
});

onUnmounted(() => {
  composer.dispose();
  document.removeEventListener('mousemove', onMouseMove);
  document.removeEventListener('touchmove', onTouchMove);
  document.removeEventListener('visibilitychange', onVisibilityChange);
});
</script>

<template lang="pug">
TresGroup
</template>
